import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import GaugeChart from "./GaugeChart";
import DiagnosticResult from "./DiagnosticResult";
import LeadCaptureForm, { LeadData } from "./LeadCaptureForm";
import { saveDiagnosticLead } from "@/lib/diagnosticService";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import diagnosticIntroBg from "@/assets/diagnostic-intro-bg.png";

interface Question {
  id: number;
  category: string;
  question: string;
  options: { value: number; label: string }[];
}

const questions: Question[] = [
  {
    id: 1,
    category: "Lealtad Patrimonial",
    question: "Si mañana la competencia baja los precios un 20%, ¿Tus clientes se mantienen con la empresa por respeto a la trayectoria de tu marca o la abandonan por una opción más económica?",
    options: [
      { value: 0, label: "La mayoría abandonaría la marca por precio." },
      { value: 5, label: "Algunos se irían, otros mantendrían la fidelidad." },
      { value: 10, label: "La mayoría permanece leal al valor de mi marca." },
    ],
  },
  {
    id: 2,
    category: "Identidad Propietaria",
    question: "Si quitamos el logo de todas las piezas de comunicación, ¿el mercado reconoce el \"sello y estilo\" de tu marca de inmediato o pasa a ser un genérico más del sector?",
    options: [
      { value: 0, label: "La comunicación de mi marca pasaría desapercibida sin el logo." },
      { value: 5, label: "Algunos reconocerían el estilo visual y comunicativo." },
      { value: 10, label: "La identidad de mi marca es inconfundible." },
    ],
  },
  {
    id: 3,
    category: "Velocidad de Capitalización",
    question: "¿Puede un inversor entender el valor de tu marca en solo 10 segundos, o la historia del negocio requiere una explicación personal para generar confianza?",
    options: [
      { value: 0, label: "Se requieren reuniones extensas para explicar el valor de mi marca." },
      { value: 5, label: "Requiere algo de contexto, pero la propuesta se entiende rápido." },
      { value: 10, label: "El valor de mi marca es evidente en segundos." },
    ],
  },
  {
    id: 4,
    category: "Premium Price",
    question: "¿Los márgenes de beneficio están consistentemente por encima de la media de la industria gracias a la reputación de tu marca?",
    options: [
      { value: 0, label: "Los márgenes son iguales o menores al promedio." },
      { value: 5, label: "A veces mi marca permite cobrar un diferencial." },
      { value: 10, label: "Mi marca permite cobrar un precio premium de forma consistente." },
    ],
  },
  {
    id: 5,
    category: "Seguridad de Activo",
    question: "¿Tu marca y tus diferenciales están legalmente blindados, o estás construyendo valor sobre un activo que no te pertenece legalmente?",
    options: [
      { value: 0, label: "No tengo protección legal de mi marca (No está registrada ni el nombre ni el logo)" },
      { value: 5, label: "Tengo algo registrado pero incompleto" },
      { value: 10, label: "Todo está legalmente protegido y blindado" },
    ],
  },
  {
    id: 6,
    category: "Brand Extension",
    question: "¿Es el nombre y reputación de tu marca suficiente para que una nueva unidad de negocio sea rentable desde el primer día, o debe financiarse el posicionamiento desde cero?",
    options: [
      { value: 0, label: "Cada nuevo proyecto debe empezar de cero." },
      { value: 5, label: "Mi marca ayuda, pero no está tan posicionada para que tenga valor de inmediato." },
      { value: 10, label: "Sí, mi marca actualmente hace rentable cualquier extensión de línea de forma acelerada." },
    ],
  },
  {
    id: 7,
    category: "Riesgo de Devaluación",
    question: "¿El flujo de ventas es orgánico gracias al prestigio de la marca o el negocio es rehén de ofertas y promociones para sostener la estructura?",
    options: [
      { value: 0, label: "Dependencia de promociones constantes para vender." },
      { value: 5, label: "Mezcla de ventas orgánicas y descuentos promocionales." },
      { value: 10, label: "Las ventas fluyen principalmente por el prestigio de marca." },
    ],
  },
  {
    id: 8,
    category: "Consistencia de Marca",
    question: "¿El mensaje actual de la empresa es una evolución estratégica de su trayectoria, o cambia según la urgencia del mercado?",
    options: [
      { value: 0, label: "El mensaje cambia constantemente sin una estrategia clara." },
      { value: 5, label: "Hay cierta consistencia, pero con variaciones frecuentes." },
      { value: 10, label: "El mensaje es una evolución coherente del activo de marca." },
    ],
  },
  {
    id: 9,
    category: "Brand Equity Interno",
    question: "¿El equipo opera con orgullo bajo la cultura de la marca, o su compromiso se limita estrictamente al cumplimiento de tareas?",
    options: [
      { value: 0, label: "El equipo solo cumple horario y tareas." },
      { value: 5, label: "Hay compromiso variable según el proyecto." },
      { value: 10, label: "El equipo vive, defiende y potencia la marca." },
    ],
  },
  {
    id: 10,
    category: "Multiplicador de Valor",
    question: "Si hoy se decidiera vender la empresa, ¿qué porcentaje de ese precio correspondería al nombre y reputación, o solo se pagaría por activos tangibles (máquinas y existencias)?",
    options: [
      { value: 0, label: "Solo pagarían por activos tangibles." },
      { value: 5, label: "Se reconocería algo de valor intangible, pero es bajo." },
      { value: 10, label: "La marca multiplica significativamente el valor de venta final." },
    ],
  },
];

const DiagnosticTest = () => {
  const [currentStep, setCurrentStep] = useState<"intro" | "test" | "contact" | "result">("intro");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleOptionSelect = (value: number) => {
    setSelectedOption(value);
  };

  const handleNext = () => {
    if (selectedOption !== null) {
      setAnswers((prev) => ({ ...prev, [currentQuestion]: selectedOption }));
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion((prev) => prev + 1);
        setSelectedOption(answers[currentQuestion + 1] ?? null);
      } else {
        // Go to contact form instead of result
        setAnswers((prev) => ({ ...prev, [currentQuestion]: selectedOption }));
        setCurrentStep("contact");
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
      setSelectedOption(answers[currentQuestion - 1] ?? null);
    }
  };

  const handleStartTest = () => {
    setCurrentStep("test");
  };

  const handleLeadSubmit = async (data: LeadData) => {
    setIsSubmitting(true);
    try {
      await saveDiagnosticLead({
        name: data.name,
        company: data.company,
        email: data.email,
        countryCode: data.countryCode,
        whatsapp: data.whatsapp,
        totalScore,
        answers,
      });
      setLeadData(data);
      setCurrentStep("result");
    } catch (error) {
      logger.error("Error saving lead:", error);
      toast({
        title: "Error",
        description: "Hubo un problema al guardar tus datos. Por favor, intentá de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestart = () => {
    setCurrentStep("intro");
    setCurrentQuestion(0);
    setAnswers({});
    setSelectedOption(null);
    setLeadData(null);
  };

  return (
    <section id="test" className={`${currentStep === "intro" ? "" : "py-20 md:py-28"}`} style={{ backgroundColor: currentStep === "intro" ? '#470628' : undefined }}>
      <div className={`${currentStep === "intro" ? "" : "container px-4"}`}>
        <AnimatePresence mode="wait">
          {/* Intro Step */}
          {currentStep === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
              style={{ background: "linear-gradient(180deg, #470628 0%, #2a0418 100%)" }}
            >
              {/* Premium container */}
              <div className="container px-4 py-16 md:py-24">
                {/* Small centered title */}
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-center mb-12 text-xs tracking-[0.25em] uppercase text-[#FF9FBD] font-semibold"
                >
                  Evaluá tu Capital de Marca
                </motion.p>

                {/* Split layout with premium card */}
                <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center max-w-6xl mx-auto">
                  {/* Left side - Content */}
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="order-2 md:order-1"
                  >
                    <div className="max-w-lg">
                      <h2 className="text-2xl md:text-3xl lg:text-4xl text-white leading-tight mb-6 font-medium tracking-tight">
                        Dedicaste tu vida a construir esta marca.
                      </h2>
                      <p className="text-base md:text-lg text-white/70 leading-relaxed mb-8">
                        No permitas que tu capital se devalúe por falta de arquitectura estratégica. Es hora de medir si estás acumulando Brand Equity o si estás financiando el crecimiento de tu competencia.
                      </p>

                      <Button
                        variant="gold"
                        size="xl"
                        onClick={handleStartTest}
                        className="group uppercase tracking-wider font-semibold"
                      >
                        Iniciar Test
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </div>
                  </motion.div>

                  {/* Right side - Premium Image Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                    className="order-1 md:order-2 flex justify-center"
                  >
                    <div className="relative">
                      {/* Decorative elements */}
                      <div className="absolute -inset-4 rounded-3xl blur-2xl" style={{ background: "linear-gradient(135deg, rgba(255, 159, 189, 0.2) 0%, transparent 50%, rgba(139, 0, 48, 0.15) 100%)" }} />
                      <div className="absolute -top-2 -right-2 w-20 h-20 rounded-full blur-xl" style={{ backgroundColor: "rgba(255, 159, 189, 0.2)" }} />
                      
                      {/* Image container */}
                      <div className="relative w-64 h-80 md:w-72 md:h-96 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10" style={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)", backgroundColor: "#1a1a1a" }}>
                        <img 
                          src={diagnosticIntroBg} 
                          alt="Capital de Marca" 
                          className="w-full h-full object-cover"
                          loading="eager"
                          decoding="async"
                          fetchPriority="high"
                        />
                        {/* Premium overlay */}
                        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(71, 6, 40, 0.3) 0%, transparent 50%, rgba(255, 159, 189, 0.1) 100%)" }} />
                      </div>
                      
                      {/* Floating accent */}
                      <div className="absolute -bottom-3 -left-3 w-24 h-24 rounded-2xl -z-10" style={{ backgroundColor: "rgba(255, 159, 189, 0.15)" }} />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === "test" && (
            <motion.div
              key="test"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="max-w-2xl mx-auto"
            >
              {/* Progress */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-muted-foreground">
                    Pregunta {currentQuestion + 1} de {questions.length}
                  </span>
                  <span className="text-sm font-medium text-primary">
                    {Math.round(progress)}%
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Question */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-card border border-border rounded-2xl p-8 mb-6">
                    <span className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
                      {questions[currentQuestion].category}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl font-semibold mb-6 text-foreground">
                      {questions[currentQuestion].question}
                    </h3>

                    <div className="space-y-3">
                      {questions[currentQuestion].options.map((option, index) => (
                        <motion.button
                          key={option.value}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          onClick={() => handleOptionSelect(option.value)}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-300 ${
                            selectedOption === option.value
                              ? "border-primary bg-primary/10 shadow-lg"
                              : "border-border hover:border-primary/50 hover:bg-muted/50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                selectedOption === option.value
                                  ? "border-primary bg-primary"
                                  : "border-muted-foreground"
                              }`}
                            >
                              {selectedOption === option.value && (
                                <CheckCircle2 className="w-4 h-4 text-primary-foreground" />
                              )}
                            </div>
                            <span
                              className={`text-base ${
                                selectedOption === option.value
                                  ? "text-foreground font-medium"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {option.label}
                            </span>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex justify-between gap-4">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <Button
                  variant="gold"
                  onClick={handleNext}
                  disabled={selectedOption === null}
                  className="gap-2"
                >
                  {currentQuestion === questions.length - 1 ? "Ver Resultado" : "Siguiente"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {currentStep === "contact" && (
            <motion.div
              key="contact"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="max-w-2xl mx-auto"
            >
              <LeadCaptureForm onSubmit={handleLeadSubmit} isSubmitting={isSubmitting} />
            </motion.div>
          )}

          {currentStep === "result" && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto"
            >
              <DiagnosticResult score={totalScore} onRestart={handleRestart} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default DiagnosticTest;
